import { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { LabelPlain } from '@components/Label';
import Avatar from '@components/Avatar';
import { Checkbox } from '@components/Checkbox';
import { getInitialsParts, getFullName, getInitialsFull, getUserContacts } from '@helpers/userName';
import { getChannelIcon } from '@helpers/channels';
import { getHumanizedDate } from '@helpers/dates';
import { MessageContentType, MessageStatus } from '@constants/inbox';
import { useTranslation } from '@hooks';
import { UserIcon, PulseAiIcon } from 'assets/images/icons';
import { CHANNEL } from '@constants/channels';

const StyledUserIcon = styled(UserIcon)({
    width: '18px !important',
    height: '18px !important',
    '& path': {
        fill: 'rgba(0, 0, 0, 0.38)',
        fillOpacity: 1,
    },
});

const StyledAvatar = styled(Avatar)({
    border: 'none',
});

const DialogWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'active' && prop !== 'read',
})(({ active, read }) => ({
    willChange: 'scroll-position',
    border: active ? '1px solid #1F4C5C' : '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: active || !read ? '#EBF6F0' : '#fff',
    padding: 8,
    borderRadius: 4,
    cursor: 'pointer',
    marginBottom: 8,
    '&:has(.AvatarSelectCheckbox:hover)': {
        borderColor: '#1F4C5C',
    },
}));

const AvatarSelectWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'allSelected',
})(({ allSelected }) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        backgroundColor: allSelected ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
    },
    '& .MuiSvgIcon-root': {
        fontSize: '1.125rem',
    },
    '& .MuiCheckbox-root': {
        padding: allSelected ? 0 : '11px',
    },
}));

const Dialog = ({ data, isActive, onSetActive, isSelected, allSelected, onSelect, users, language, isMobile }) => {
    const [isHover, setIsHover] = useState(false);
    const [, setTime] = useState(new Date());

    const { t } = useTranslation();

    useEffect(() => {
        const interval = setInterval(() => setTime(new Date()), 30000);
        return () => clearInterval(interval);
    }, []);

    const lastMessage = {
        [MessageContentType.Text]: data.lastMessage,
        [MessageContentType.Image]: t('common.image'),
        [MessageContentType.File]: t('common.file'),
        [MessageContentType.Video]: t('common.video'),
        [MessageContentType.Audio]: t('common.audio'),
        [MessageContentType.StoryImage]: t('common.story'),
        [MessageContentType.StoryVideo]: t('common.story'),
    };

    const channelName = data?.channelId === CHANNEL.ARCHIVED ? t('common.archived') : data?.channelName;

    const assignedUser = useMemo(() => {
        return users.find(user => user.id === data.assignedUserId);
    }, [data.assignedUserId, users]);

    const userContacts = useMemo(() => {
        return getUserContacts(
            {
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber,
                email: data.email,
                externalId: data.contactExternalId,
            },
            '-',
        );
    }, [data]);

    return (
        <DialogWrapper
            active={isActive}
            onClick={onSetActive}
            read={data.read}
            onMouseOver={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <div style={{ display: 'grid', gridGap: '0 8px', gridTemplateColumns: '40px calc(100% - 48px)' }}>
                {(isHover && !isMobile) || isSelected ? (
                    <AvatarSelectWrapper onClick={e => e.stopPropagation()} allSelected={allSelected}>
                        <Checkbox
                            className="AvatarSelectCheckbox"
                            checked={isSelected}
                            onChange={onSelect}
                            disabled={allSelected}
                        />
                    </AvatarSelectWrapper>
                ) : (
                    <Avatar
                        size="large"
                        fullName={getFullName(data?.firstName, data?.lastName)}
                        label={getInitialsParts(data?.firstName, data?.lastName)}
                        icon={<StyledUserIcon />}
                        withBadge
                        badge={getChannelIcon(
                            data.channelId === CHANNEL.ARCHIVED ? CHANNEL.ARCHIVED : data.channelType,
                        )}
                        onClick={
                            isMobile
                                ? e => {
                                      e.stopPropagation();
                                      onSelect();
                                  }
                                : null
                        }
                    />
                )}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'rgba(0, 0, 0, 0.6)',
                                marginBottom: '2px',
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {channelName}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'rgba(0, 0, 0, 0.6)',
                                textAlign: 'end',
                                whiteSpace: 'nowrap',
                                '&:first-letter': {
                                    textTransform: 'capitalize',
                                },
                            }}
                        >
                            {getHumanizedDate(data.lastActivityAt, language)}
                        </Typography>
                    </div>
                    <Typography
                        variant="subtitle2"
                        sx={{ marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {userContacts}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(0, 0, 0, 0.6)',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontStyle:
                                data.lastMessageStatus === MessageStatus.Deleted ? 'italic !important' : 'normal',
                        }}
                    >
                        {data.lastMessageStatus === MessageStatus.Deleted
                            ? t('InboxPage.messageDeleted')
                            : lastMessage[data.lastMessageContentType]}
                    </Typography>
                </div>
            </div>
            <div
                style={{
                    marginTop: data.labels?.length ? '8px' : 0,
                    display: 'grid',
                    gridTemplateColumns:
                        assignedUser || data?.assignedAgentId ? 'calc(100% - 28px) 20px' : 'calc(100%) 0px',
                    gridGap: '8px',
                    alignItems: 'baseline',
                }}
            >
                <div style={{ overflow: 'hidden', display: 'flex' }}>
                    {data.labels?.length
                        ? data.labels.map(label => (
                              <LabelPlain key={label.id} type={label.name} variant="caption" size="small">
                                  {label.name}
                              </LabelPlain>
                          ))
                        : null}
                </div>
                {(assignedUser || data?.assignedAgentId) && (
                    <StyledAvatar
                        size="xSmall"
                        fullName={`${assignedUser?.firstName} ${assignedUser?.lastName}`}
                        label={
                            data?.assignedAgentId
                                ? null
                                : getInitialsFull(`${assignedUser?.firstName} ${assignedUser?.lastName}`)
                        }
                        icon={data?.assignedAgentId ? <PulseAiIcon /> : <StyledUserIcon />}
                    />
                )}
            </div>
        </DialogWrapper>
    );
};

export default Dialog;
